/**
 * Who has access to a resource I own, add a colleague, revoke — over the
 * owner-sharing surface (`/api/v1/shares`, ADR-0015). Generic over the resource
 * type so other owned resources can reuse it; the Collections detail page is
 * the first user (`resource_type = document_index`, permission `read`).
 *
 * Every grant and revocation carries a reason and is audited server-side
 * (`resource.access_granted` / `resource.access_revoked`). The backend
 * re-checks ownership on every call; this panel only offers the affordance.
 *
 * Names: the user list masks names and emails for non-administrators (only the
 * id is returned), so a Developer sees an opaque label. Resolving colleague
 * names for sharing is a backend decision tracked separately.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconTrash, IconUserPlus } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { createShare, listShares, revokeShare, type ShareOut } from "@/shared/api/shares";
import { listUsers, type UserOut } from "@/shared/api/users";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

interface Props {
  resourceType: string;
  resourceId: string;
  /** The owner never needs a grant, so they are left out of the picker. */
  ownerUserId: string;
  /** Noun for copy, e.g. "collection". */
  noun: string;
}

function userLabel(u: UserOut | undefined, userId: string): string {
  if (!u) return `User …${userId.slice(-6)}`;
  return u.display_name || u.email || `User …${userId.slice(-6)}`;
}

export function AccessPanel({ resourceType, resourceId, ownerUserId, noun }: Props) {
  const getToken = useAccessToken();
  const [shares, setShares] = useState<ShareOut[] | null>(null);
  const [users, setUsers] = useState<UserOut[]>([]);
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const [s, u] = await Promise.all([
        listShares(token, resourceType, resourceId),
        listUsers(token, { limit: 200, status: "active" }).catch(() => ({ items: [] })),
      ]);
      setShares(s.items.filter((g) => g.revoked_at_utc === null));
      setUsers(u.items);
    } catch (err) {
      toastApiError("Could not load access", err);
      setShares([]);
    }
  }, [getToken, resourceType, resourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const byId = useMemo(() => new Map(users.map((u) => [u.user_id, u])), [users]);
  // A grant names a subject (ADR-0028): a user, or a role. Only user subjects
  // are offered here; a role share made elsewhere still lists, by its role.
  const holders = useMemo(
    () => new Set((shares ?? []).filter((s) => s.subject_type === "user").map((s) => s.subject_id)),
    [shares],
  );
  const holderLabel = (s: ShareOut) =>
    s.subject_type === "user"
      ? userLabel(byId.get(s.subject_id), s.subject_id)
      : `Everyone: ${s.subject_id}`;
  const options = users
    .filter((u) => u.user_id !== ownerUserId && !holders.has(u.user_id))
    .map((u) => ({ value: u.user_id, label: userLabel(u, u.user_id) }));

  const add = async () => {
    if (!pick) return;
    const reason = await promptForReason(`Share this ${noun}`, "Reason for sharing");
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await createShare(token, {
        resource_type: resourceType,
        resource_id: resourceId,
        subject_type: "user",
        subject_id: pick,
        permission: "read",
        reason,
      });
      toastSuccess("Shared", `${userLabel(byId.get(pick), pick)} can now open this ${noun}.`);
      setPick(null);
      await load();
    } catch (err) {
      toastApiError("Could not share", err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (share: ShareOut) => {
    const reason = await promptForReason("Revoke access", "Reason for revoking");
    if (!reason) return;
    try {
      const token = await getToken();
      await revokeShare(token, share.grant_id, reason);
      toastSuccess("Access revoked", `${holderLabel(share)}.`);
      await load();
    } catch (err) {
      toastApiError("Could not revoke", err);
    }
  };

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="sm">
        <Title order={4}>Access</Title>
        <Text size="sm" c="dimmed">
          Private by default. People you share with can open this {noun} and its documents, and ask
          questions of it. They cannot change or delete it.
        </Text>
        {shares === null ? (
          <Text size="sm" c="dimmed">
            Loading…
          </Text>
        ) : shares.length === 0 ? (
          <Text size="sm" data-testid="access-empty">
            Only you.
          </Text>
        ) : (
          <Stack gap={4} data-testid="access-list">
            {shares.map((s) => (
              <Group key={s.grant_id} justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm">{holderLabel(s)}</Text>
                  <Badge size="xs" variant="light">
                    {s.permission}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    since {new Date(s.granted_at_utc).toLocaleDateString()}
                  </Text>
                </Group>
                <Tooltip label="Revoke">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`Revoke ${holderLabel(s)}`}
                    onClick={() => void revoke(s)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ))}
          </Stack>
        )}
        <Group gap="xs" align="flex-end">
          <Select
            label="Add a person"
            placeholder="Choose a user"
            data={options}
            value={pick}
            onChange={setPick}
            searchable
            nothingFoundMessage="No one else to add"
            style={{ flex: 1 }}
          />
          <Button
            leftSection={<IconUserPlus size={16} />}
            onClick={() => void add()}
            loading={busy}
            disabled={!pick}
          >
            Share
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
